import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getAgentSession as getAgentSessionService,
  getValidation as getValidationService,
  sendAgentTurn as sendAgentTurnService,
  submitForm as submitFormService,
} from './service';

export const loadAgentSession = createAsyncThunk(
  'partAAgent/loadSession',
  async ({ token }, { rejectWithValue }) => {
    try {
      return await getAgentSessionService(token);
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: error.message });
    }
  }
);

export const sendAgentTurn = createAsyncThunk(
  'partAAgent/sendTurn',
  async ({ token, turn }, { rejectWithValue }) => {
    try {
      return await sendAgentTurnService(token, turn);
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: error.message });
    }
  }
);

export const submitAgentForm = createAsyncThunk(
  'partAAgent/submit',
  async ({ token }, { rejectWithValue }) => {
    try {
      const validation = await getValidationService(token);
      if (!validation.valid) {
        return rejectWithValue({
          message: validation.issues?.[0]?.message || 'Some answers need correction.',
          validation,
        });
      }
      await submitFormService(token);
      return {};
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: error.message });
    }
  }
);

const initialState = {
  session: null,
  isLoading: false,
  isSending: false,
  isSubmitting: false,
  isSubmitted: false,
  error: null,
  lastTurn: null,
};

const partAAgentSlice = createSlice({
  name: 'partAAgent',
  initialState,
  reducers: {
    resetAgent: () => initialState,
    clearAgentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAgentSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadAgentSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.session = action.payload;
      })
      .addCase(loadAgentSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error;
      })
      .addCase(sendAgentTurn.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        state.lastTurn = action.meta.arg.turn;
      })
      .addCase(sendAgentTurn.fulfilled, (state, action) => {
        state.isSending = false;
        state.session = action.payload;
        state.lastTurn = null;
      })
      .addCase(sendAgentTurn.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload || action.error;
      })
      .addCase(submitAgentForm.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitAgentForm.fulfilled, (state) => {
        state.isSubmitting = false;
        state.isSubmitted = true;
      })
      .addCase(submitAgentForm.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || action.error;
      });
  },
});

export const { clearAgentError, resetAgent } = partAAgentSlice.actions;
export default partAAgentSlice.reducer;
